import React, { useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Text,
  useOutsideClick,
  VStack,
} from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";

// Generic single-column multi-select pill. Mirrors the TaskFilterBar style
// on the Tasks list page.
function FilterPill({ label, options, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        {label}
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="220px"
          maxH="300px"
          overflowY="auto"
          py={2}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <VStack align="start" spacing={0} px={3}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                size="sm"
                colorScheme="limeShade"
                isChecked={selected.some((s) => s.value === option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selected, option]);
                  } else {
                    onChange(selected.filter((s) => s.value !== option.value));
                  }
                }}
                py={1.5}
                w="100%"
              >
                <Text fontSize="13px" color="gray.600">
                  {option.label}
                </Text>
              </Checkbox>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

// Combined "Dimension" pill: one dropdown that groups items by dimension
// level, so users don't have to open three pills to filter across
// Region + Country + Site. Mirrors DimensionFilterPill on TaskFilterBar.
function DimensionFilterPill({ dimensionGroups, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        Dimension
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="220px"
          maxH="300px"
          overflowY="auto"
          py={2}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <VStack align="start" spacing={0} px={3}>
            {dimensionGroups.map((group, gIdx) => (
              <React.Fragment key={group.dimKey}>
                {gIdx > 0 && <Box w="100%" borderTop="1px solid" borderColor="gray.100" my={1} />}
                <Text fontSize="12px" fontWeight="700" color="gray.700" textTransform="uppercase" pt={gIdx > 0 ? 1 : 0} pb={1}>
                  {group.name}
                </Text>
                {group.items.map((item) => {
                  const optionKey = `${group.dimKey}:${item.label}`;
                  return (
                    <Checkbox
                      key={optionKey}
                      size="sm"
                      colorScheme="limeShade"
                      isChecked={selected.some((s) => s.value === optionKey)}
                      onChange={(e) => {
                        const option = { label: item.label, value: optionKey, dimKey: group.dimKey };
                        if (e.target.checked) {
                          onChange([...selected, option]);
                        } else {
                          onChange(selected.filter((s) => s.value !== optionKey));
                        }
                      }}
                      py={1.5}
                      w="100%"
                    >
                      <Text fontSize="13px" color="gray.600">
                        {item.label}
                      </Text>
                    </Checkbox>
                  );
                })}
              </React.Fragment>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

// Top-level bar.
// `columns` is a list of non-dimension filter columns to expose, in order.
//   [{ key, label, options: [{label,value}] }]
// `dimensionGroups` is the grouped list for the Dimension pill.
//   [{ dimKey, name, items: [{label,value}] }]
// `filters` holds the current selection keyed by column key; the Dimension
// value is stored under key `_dimension`.
function DimensionsGridFilterBar({
  dimensionGroups,
  columns,
  filters,
  onFilterChange,
  onClearAll,
  filteredCount,
  totalCount,
}) {
  const activeCount =
    (filters._dimension?.length || 0) +
    columns.reduce((acc, col) => acc + (filters[col.key]?.length || 0), 0);

  const showDimensionPill = dimensionGroups.length > 0;
  const hasAnyFilterable = showDimensionPill || columns.length > 0;
  if (!hasAnyFilterable) return null;

  const hasActiveFilter = activeCount > 0;

  return (
    <Flex gap={2} align="center" flexWrap="wrap" mb={3}>
      {showDimensionPill && (
        <DimensionFilterPill
          dimensionGroups={dimensionGroups}
          selected={filters._dimension || []}
          onChange={(next) => onFilterChange("_dimension", next)}
        />
      )}
      {columns.map((col) => (
        <FilterPill
          key={col.key}
          label={col.label}
          options={col.options}
          selected={filters[col.key] || []}
          onChange={(next) => onFilterChange(col.key, next)}
        />
      ))}
      {hasActiveFilter && (
        <Text
          as="button"
          fontSize="13px"
          color="limeShade.600"
          fontWeight="500"
          _hover={{ color: "limeShade.700" }}
          onClick={onClearAll}
        >
          Clear filters
        </Text>
      )}
      {hasActiveFilter && typeof filteredCount === "number" && typeof totalCount === "number" && (
        <Text fontSize="13px" color="gray.500">
          {filteredCount} of {totalCount} items
        </Text>
      )}
    </Flex>
  );
}

export default DimensionsGridFilterBar;
